import type { ReactElement, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { TooltipRenderProps } from "react-joyride";

import { Button } from "@unified-checkout/design-system";
import {
  ChevronLeft,
  ChevronRight,
  Xmark
} from "@unified-checkout/design-system/icons/index";
import type { IButton } from "libs/design-system/src/types/Button/button";

import { useLangStore } from "@app/dashboard/store/lang";
import { Stepper } from "../components/Stepper";
import { ReactComponent as Clouds } from "../assets/solid-clouds.svg";
import styles from "./step.module.scss";


export const Step = ({
  step,
  totalNumberOfSteps,
  featureName,
  featureIcon,
  desc,
  label,
  content,
  joyRideProps
}: {
  step: number,
  totalNumberOfSteps: number,
  featureName: string,
  featureIcon: ReactElement<'svg'>,
  desc?: string,
  label: string,
  content?: ReactNode,
  joyRideProps?: TooltipRenderProps
}) => {
  const { lang } = useLangStore();
  const { t } = useTranslation();

  const {
    skipProps,
    primaryProps,
    backProps
  } = joyRideProps || {};

  const actionBtnsProps: IButton[] = [
    ...(
      step === 1 ? [] :
        [{
          id: "back",
          size: "lg",
          color: "secondary",
          variant: "outlined",
          text: t('innerLayout.back'),
          leadingIcon: lang === "ar" ? ChevronRight : ChevronLeft,
        }] as IButton[]
    ),
    {
      id: "next",
      size: "lg",
      text: step === totalNumberOfSteps ? t('innerLayout.end') : t('innerLayout.next'),
      trailingIcon: lang === "ar" ? ChevronLeft : ChevronRight,
    }
  ];

  return (
    <div className={styles["step"]}>

      <div className={styles["step__illustrations-box"]}>

        <div className={styles["step__illustrations-box__x-btn"]} {...skipProps}>
          <Button
            size="sm"
            color="secondary"
            variant="outlined"
            leadingIcon={Xmark}
          />
        </div>

        <div className={styles["step__illustrations-box__feature-icon-wrapper"]}>
          {featureIcon}
        </div>

        <div className={styles["step__illustrations-box__clouds"]}>
          <Clouds />
        </div>

      </div>

      <div className={styles["step__main"]}>
        <Stepper
          step={step}
          featureName={featureName}
          totalNumberOfSteps={totalNumberOfSteps}
          desc={desc}
        />

        <div className={styles["step__main__text-box"]}>

          <div className={styles["step__main__text-box__label"]}>
            {label}
          </div>

          {
            content &&
            <div className={styles["step__main__text-box__content"]}>
              {content}
            </div>
          }
        </div>

      </div>

      <div className={styles["step__actions"]}>
        {
          actionBtnsProps.map((btn, i) =>
            <div
              key={i}
              className={styles["step__actions__action-btn"]}
              {...btn.id === "next" ? { ...primaryProps } : { ...backProps }}
              title=""
            >
              <Button {...btn} />
            </div>
          )
        }
      </div>
    </div>
  )
}
